import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Upload, X, ZoomIn, Download, FileDown, Folder } from "lucide-react";

// Component for images that need authentication
function ImageWithAuth({ src, alt, className, token }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await axios.get(src, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        });
        const imageObjectURL = URL.createObjectURL(response.data);
        setImageSrc(imageObjectURL);
      } catch (err) {
        console.error('Failed to load image:', err);
        setError(true);
      }
    };

    if (src && token) {
      fetchImage();
    }

    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src, token]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 text-sm p-4 text-center">
        Bild konnte nicht geladen werden
      </div>
    );
  }

  if (!imageSrc) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400"></div>
      </div>
    );
  }

  return <img src={imageSrc} alt={alt} className={className} />;
}

export default function ArticleFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    article_number: "",
    price: "",
    weight: "",
    unit: "",
    color: "",
    description: "",
    categories: [],
    accessories: [],
    variants: [],
    related_products: [],
  });
  const [images, setImages] = useState([]);
  const [sequentialId, setSequentialId] = useState(null);
  const [showAccessories, setShowAccessories] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [showRelatedProducts, setShowRelatedProducts] = useState(false);
  const [accessoriesSearch, setAccessoriesSearch] = useState("");
  const [relatedSearch, setRelatedSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [accessoriesData, setAccessoriesData] = useState([]);
  const [relatedProductsData, setRelatedProductsData] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [deleteImageDialog, setDeleteImageDialog] = useState({ open: false, imageId: null });
  const [downloadDialog, setDownloadDialog] = useState({ open: false, type: null });
  const [selectedSizes, setSelectedSizes] = useState({
    original: true,
    medium: true,
    thumbnail: true,
  });

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchArticle();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Kategorien");
    }
  };

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      const article = response.data;
      setFormData({
        name: article.name,
        article_number: article.article_number,
        price: article.price,
        weight: article.weight,
        unit: article.unit,
        color: article.color,
        description: article.description,
        categories: article.categories || [],
        accessories: article.accessories || [],
        variants: article.variants || [],
        related_products: article.related_products || [],
      });
      setSequentialId(article.sequential_id);
      setImages(article.images || []);
      setShowAccessories(article.accessories && article.accessories.length > 0);
      setShowVariants(article.variants && article.variants.length > 0);
      setShowRelatedProducts(article.related_products && article.related_products.length > 0);
      
      // Fetch full article data for accessories and related products
      if (article.accessories && article.accessories.length > 0) {
        fetchArticlesByIds(article.accessories, setAccessoriesData);
      }
      if (article.related_products && article.related_products.length > 0) {
        fetchArticlesByIds(article.related_products, setRelatedProductsData);
      }
    } catch (error) {
      toast.error("Fehler beim Laden des Artikels");
      navigate("/articles");
    }
  };

  const fetchArticlesByIds = async (ids, setData) => {
    try {
      const response = await axios.get(`${API}/articles`);
      const allArticles = response.data;
      const filtered = allArticles.filter(art => ids.includes(art.id));
      setData(filtered);
    } catch (error) {
      console.error("Error fetching articles by IDs:", error);
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter((id) => id !== categoryId)
        : [...prev.categories, categoryId],
    }));
  };

  // Search articles for auto-complete
  const searchArticles = async (query, type) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/articles/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  // Add accessory
  const addAccessory = (article) => {
    if (!formData.accessories.includes(article.id)) {
      setFormData(prev => ({
        ...prev,
        accessories: [...prev.accessories, article.id]
      }));
      setAccessoriesData(prev => [...prev, article]);
    }
    setAccessoriesSearch("");
    setSearchResults([]);
  };

  // Remove accessory
  const removeAccessory = (articleId) => {
    setFormData(prev => ({
      ...prev,
      accessories: prev.accessories.filter(id => id !== articleId)
    }));
    setAccessoriesData(prev => prev.filter(art => art.id !== articleId));
  };

  // Add variant
  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { name: "", description: "" }]
    }));
  };

  // Update variant
  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  // Remove variant
  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Add related product
  const addRelatedProduct = (article) => {
    if (!formData.related_products.includes(article.id)) {
      setFormData(prev => ({
        ...prev,
        related_products: [...prev.related_products, article.id]
      }));
      setRelatedProductsData(prev => [...prev, article]);
    }
    setRelatedSearch("");
    setSearchResults([]);
  };

  // Remove related product
  const removeRelatedProduct = (articleId) => {
    setFormData(prev => ({
      ...prev,
      related_products: prev.related_products.filter(id => id !== articleId)
    }));
    setRelatedProductsData(prev => prev.filter(art => art.id !== articleId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        weight: parseFloat(formData.weight),
      };

      if (isEdit) {
        await axios.put(`${API}/articles/${id}`, data);
        toast.success("Artikel erfolgreich aktualisiert");
      } else {
        await axios.post(`${API}/articles`, data);
        toast.success("Artikel erfolgreich erstellt");
      }
      navigate("/articles");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern des Artikels");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!isEdit) {
      toast.error("Bitte speichern Sie zuerst den Artikel, bevor Sie Bilder hochladen");
      return;
    }

    if (images.length + files.length > 10) {
      toast.error("Maximal 10 Bilder pro Artikel erlaubt");
      return;
    }

    setUploading(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(`${API}/articles/${id}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setImages((prev) => [...prev, response.data]);
        toast.success(`${file.name} erfolgreich hochgeladen`);
      } catch (error) {
        toast.error(`Fehler beim Hochladen von ${file.name}`);
      }
    }

    setUploading(false);
  };

  const handleDeleteImage = async () => {
    try {
      await axios.delete(`${API}/articles/${id}/images/${deleteImageDialog.imageId}`);
      setImages((prev) => prev.filter((img) => img.id !== deleteImageDialog.imageId));
      toast.success("Bild erfolgreich gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen des Bildes");
    } finally {
      setDeleteImageDialog({ open: false, imageId: null });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleDownloadImages = async () => {
    const sizesArray = Object.keys(selectedSizes).filter(key => selectedSizes[key]);
    
    if (sizesArray.length === 0) {
      toast.error("Bitte wählen Sie mindestens eine Auflösung");
      return;
    }

    try {
      const params = new URLSearchParams();
      sizesArray.forEach(size => params.append('sizes', size));
      
      const response = await axios.get(`${API}/articles/${id}/images-zip?${params.toString()}`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${formData.article_number}_images.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Bilder erfolgreich heruntergeladen");
      setDownloadDialog({ open: false, type: null });
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const handleDownloadCSV = async () => {
    try {
      // Create CSV for single article
      const categoryNames = formData.categories
        .map(catId => categories.find(c => c.id === catId)?.name)
        .filter(Boolean)
        .join(", ");
      
      const variantsText = formData.variants.map(v => `${v.name}: ${v.description || ''}`).join('; ');
      
      // Get article numbers for accessories and related products
      const accessoriesNumbers = accessoriesData.map(a => a.article_number || a.sequential_id).join(', ');
      const relatedNumbers = relatedProductsData.map(a => a.article_number || a.sequential_id).join(', ');
      
      const csvContent = `Fortlaufende ID,Kurzbeschreibung,Artikelnummer,Preis (netto),Gewicht,Mengeneinheit,Farbe,Artikelbeschreibung,Kategorien,Mögliches Zubehör,Varianten,Verwandte Produkte,Anzahl Bilder\n`
        + `${sequentialId || ""},"${formData.name}","${formData.article_number}",${formData.price},${formData.weight},"${formData.unit || ""}","${formData.color}","${formData.description}","${categoryNames}","${accessoriesNumbers}","${variantsText}","${relatedNumbers}",${images.length}`;
      
      const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${formData.article_number}_artikel.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV erfolgreich heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim CSV Export");
    }
  };

  const getCategoryHierarchy = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return null;
    
    if (category.parent_id) {
      const parent = categories.find(c => c.id === category.parent_id);
      return parent ? `${parent.name} → ${category.name}` : category.name;
    }
    return category.name;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/articles")}
              variant="outline"
              size="icon"
              data-testid="back-button"
              className="border-slate-300"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEdit ? "Artikel bearbeiten" : "Neuer Artikel"}
              </h1>
              <p className="text-sm text-slate-600">
                {isEdit ? "Artikel-Daten aktualisieren" : "Neuen Artikel erstellen"}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Article Details */}
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Artikelinformationen</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {isEdit && sequentialId && (
                <div className="md:col-span-2">
                  <Label htmlFor="sequential_id" className="text-slate-700 font-medium">
                    Fortlaufende ID
                  </Label>
                  <Input
                    id="sequential_id"
                    value={sequentialId}
                    disabled
                    className="mt-2 h-11 border-slate-300 bg-slate-100 cursor-not-allowed font-mono"
                  />
                </div>
              )}
              
              <div className="md:col-span-2">
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  Kurzbeschreibung *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="article-name-input"
                  className="mt-2 h-11 border-slate-300"
                  placeholder="z.B. Premium Schreibtisch"
                />
              </div>

              <div>
                <Label htmlFor="article_number" className="text-slate-700 font-medium">
                  Artikelnummer
                </Label>
                <Input
                  id="article_number"
                  value={formData.article_number || ""}
                  onChange={(e) => setFormData({ ...formData, article_number: e.target.value })}
                  data-testid="article-number-input"
                  className="mt-2 h-11 border-slate-300"
                  placeholder="z.B. ART-12345"
                />
              </div>

              <div>
                <Label htmlFor="price" className="text-slate-700 font-medium">
                  Preis (netto)
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price || ""}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  data-testid="article-price-input"
                  className="mt-2 h-11 border-slate-300"
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="weight" className="text-slate-700 font-medium">
                  Gewicht (kg)
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.01"
                  value={formData.weight || ""}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  data-testid="article-weight-input"
                  className="mt-2 h-11 border-slate-300"
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="unit" className="text-slate-700 font-medium">
                  Mengeneinheit
                </Label>
                <Input
                  id="unit"
                  type="text"
                  value={formData.unit || ""}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  data-testid="article-unit-input"
                  className="mt-2 h-11 border-slate-300"
                  placeholder="z.B. Stück, Liter, Set, kg"
                />
              </div>

              <div>
                <Label htmlFor="color" className="text-slate-700 font-medium">
                  Farbe
                </Label>
                <Input
                  id="color"
                  value={formData.color || ""}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  data-testid="article-color-input"
                  className="mt-2 h-11 border-slate-300"
                  placeholder="z.B. Schwarz"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-slate-700 font-medium">
                  Artikelbeschreibung
                </Label>
                <Textarea
                  id="description"
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="article-description-input"
                  className="mt-2 min-h-[120px] border-slate-300 resize-none"
                  placeholder="Beschreiben Sie den Artikel..."
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-slate-700 font-medium mb-3 block">
                  <Folder className="w-4 h-4 inline mr-2" />
                  Kategorien
                </Label>
                
                {/* Selected Categories Display */}
                {formData.categories.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {formData.categories.map((catId) => {
                      const hierarchy = getCategoryHierarchy(catId);
                      return hierarchy ? (
                        <Badge
                          key={catId}
                          variant="secondary"
                          className="px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          {hierarchy}
                          <X
                            className="w-3 h-3 ml-2 cursor-pointer"
                            onClick={() => toggleCategory(catId)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                
                {/* Category Selection */}
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                  {categories.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      Keine Kategorien verfügbar. Erstellen Sie zuerst Kategorien.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {categories
                        .filter(cat => !cat.parent_id)
                        .map((parentCat) => (
                          <div key={parentCat.id} className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`category-${parentCat.id}`}
                                checked={formData.categories.includes(parentCat.id)}
                                onCheckedChange={() => toggleCategory(parentCat.id)}
                                data-testid={`category-checkbox-${parentCat.id}`}
                              />
                              <label
                                htmlFor={`category-${parentCat.id}`}
                                className="text-sm font-semibold text-slate-900 cursor-pointer"
                              >
                                {parentCat.name}
                              </label>
                            </div>
                            
                            {/* Subcategories */}
                            {categories
                              .filter(cat => cat.parent_id === parentCat.id)
                              .map((subCat) => (
                                <div key={subCat.id} className="flex items-center space-x-2 ml-6">
                                  <Checkbox
                                    id={`category-${subCat.id}`}
                                    checked={formData.categories.includes(subCat.id)}
                                    onCheckedChange={() => toggleCategory(subCat.id)}
                                    data-testid={`category-checkbox-${subCat.id}`}
                                  />
                                  <label
                                    htmlFor={`category-${subCat.id}`}
                                    className="text-sm text-slate-700 cursor-pointer"
                                  >
                                    → {subCat.name}
                                  </label>
                                </div>
                              ))
                            }
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* New Fields: Accessories, Variants, Related Products */}
            <div className="mt-8 space-y-6 border-t border-slate-200 pt-6">
              {/* Mögliches Zubehör */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="show-accessories"
                    checked={showAccessories}
                    onCheckedChange={(checked) => {
                      setShowAccessories(checked);
                      if (!checked) {
                        setFormData(prev => ({ ...prev, accessories: [] }));
                      }
                    }}
                  />
                  <Label htmlFor="show-accessories" className="text-slate-700 font-medium cursor-pointer">
                    Mögliches Zubehör
                  </Label>
                </div>

                {showAccessories && (
                  <div className="space-y-4 ml-6">
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Artikel suchen (Name oder Nummer)..."
                        value={accessoriesSearch}
                        onChange={(e) => {
                          setAccessoriesSearch(e.target.value);
                          searchArticles(e.target.value, 'accessories');
                        }}
                        className="h-11 border-slate-300"
                      />
                      {searchResults.length > 0 && accessoriesSearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {searchResults.map((article) => (
                            <div
                              key={article.id}
                              className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                              onClick={() => addAccessory(article)}
                            >
                              <div className="font-medium text-slate-900">
                                #{article.sequential_id} - {article.name}
                              </div>
                              {article.article_number && (
                                <div className="text-sm text-slate-500">{article.article_number}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {formData.accessories.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.accessories.map((accId) => {
                          const acc = accessoriesData.find(a => a.id === accId);
                          return (
                            <Badge
                              key={accId}
                              variant="secondary"
                              className="px-3 py-1.5 bg-blue-100 text-blue-700"
                            >
                              {acc ? `#${acc.sequential_id} - ${acc.name}` : accId}
                              <X
                                className="w-3 h-3 ml-2 cursor-pointer"
                                onClick={() => removeAccessory(accId)}
                              />
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Varianten */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="show-variants"
                    checked={showVariants}
                    onCheckedChange={(checked) => {
                      setShowVariants(checked);
                      if (!checked) {
                        setFormData(prev => ({ ...prev, variants: [] }));
                      } else if (formData.variants.length === 0) {
                        addVariant();
                      }
                    }}
                  />
                  <Label htmlFor="show-variants" className="text-slate-700 font-medium cursor-pointer">
                    Varianten
                  </Label>
                </div>

                {showVariants && (
                  <div className="space-y-4 ml-6">
                    {formData.variants.map((variant, index) => (
                      <div key={index} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        <div className="flex justify-between items-center mb-3">
                          <Label className="text-slate-700 font-medium">Variante {index + 1}</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeVariant(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm text-slate-600">Name</Label>
                            <Input
                              type="text"
                              value={variant.name}
                              onChange={(e) => updateVariant(index, 'name', e.target.value)}
                              className="mt-1 h-10 border-slate-300"
                              placeholder="z.B. Farbe Rot, Größe L"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-slate-600">Beschreibung</Label>
                            <Textarea
                              value={variant.description || ''}
                              onChange={(e) => updateVariant(index, 'description', e.target.value)}
                              className="mt-1 border-slate-300 min-h-[80px]"
                              placeholder="Beschreibung der Variante..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addVariant}
                      className="w-full border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Weitere Variante hinzufügen
                    </Button>
                  </div>
                )}
              </div>

              {/* Verwandte Produkte */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="show-related"
                    checked={showRelatedProducts}
                    onCheckedChange={(checked) => {
                      setShowRelatedProducts(checked);
                      if (!checked) {
                        setFormData(prev => ({ ...prev, related_products: [] }));
                      }
                    }}
                  />
                  <Label htmlFor="show-related" className="text-slate-700 font-medium cursor-pointer">
                    Verwandte Produkte
                  </Label>
                </div>

                {showRelatedProducts && (
                  <div className="space-y-4 ml-6">
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Artikel suchen (Name oder Nummer)..."
                        value={relatedSearch}
                        onChange={(e) => {
                          setRelatedSearch(e.target.value);
                          searchArticles(e.target.value, 'related');
                        }}
                        className="h-11 border-slate-300"
                      />
                      {searchResults.length > 0 && relatedSearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {searchResults.map((article) => (
                            <div
                              key={article.id}
                              className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                              onClick={() => addRelatedProduct(article)}
                            >
                              <div className="font-medium text-slate-900">
                                #{article.sequential_id} - {article.name}
                              </div>
                              {article.article_number && (
                                <div className="text-sm text-slate-500">{article.article_number}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {formData.related_products.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.related_products.map((relId) => {
                          const rel = relatedProductsData.find(a => a.id === relId);
                          return (
                            <Badge
                              key={relId}
                              variant="secondary"
                              className="px-3 py-1.5 bg-green-100 text-green-700"
                            >
                              {rel ? `#${rel.sequential_id} - ${rel.name}` : relId}
                              <X
                                className="w-3 h-3 ml-2 cursor-pointer"
                                onClick={() => removeRelatedProduct(relId)}
                              />
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/articles")}
                data-testid="cancel-button"
                className="border-slate-300 h-11 px-6"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="save-article-button"
                className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6"
              >
                {loading ? "Wird gespeichert..." : isEdit ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </div>

          {/* Downloads Section - Only in edit mode */}
          {isEdit && (
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Downloads</h2>
              <p className="text-sm text-slate-600 mb-6">Artikel-Daten und Bilder exportieren</p>
              
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  onClick={handleDownloadCSV}
                  variant="outline"
                  className="border-slate-300 hover:bg-slate-100"
                  data-testid="download-article-csv"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  CSV Export
                </Button>
                
                {images.length > 0 && (
                  <Button
                    type="button"
                    onClick={() => setDownloadDialog({ open: true, type: 'images' })}
                    variant="outline"
                    className="border-slate-300 hover:bg-slate-100"
                    data-testid="download-article-images"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Bilder herunterladen ({images.length})
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Image Upload Section - Only shown in edit mode */}
          {isEdit && (
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Bilder</h2>
              <p className="text-sm text-slate-600 mb-6">Maximal 10 Bilder pro Artikel</p>

              {/* Image Grid - Show ABOVE upload area */}
              {images.length > 0 && (
                <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {images.map((image) => {
                    return (
                      <div
                        key={image.id}
                        className="relative group aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 cursor-pointer"
                        data-testid={`image-preview-${image.id}`}
                        onClick={() => setPreviewImage(image)}
                      >
                        <ImageWithAuth
                          src={`${API}/articles/${id}/images/${image.id}/thumbnail?t=${Date.now()}`}
                          alt={image.filename}
                          className="w-full h-full object-cover"
                          token={localStorage.getItem("token")}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2">
                          <Button
                            type="button"
                            size="icon"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(image);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-white hover:bg-slate-100"
                            data-testid={`preview-image-${image.id}`}
                          >
                            <ZoomIn className="w-4 h-4 text-slate-900" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteImageDialog({ open: true, imageId: image.id });
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-image-${image.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <p className="text-white text-xs truncate">{image.filename}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-slate-300 bg-slate-50 hover:border-slate-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                data-testid="image-dropzone"
              >
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-700 font-medium mb-2">Bilder per Drag & Drop hochladen</p>
                <p className="text-sm text-slate-500 mb-4">oder</p>
                <label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <Button type="button" variant="outline" className="border-slate-300" asChild>
                    <span>Dateien auswählen</span>
                  </Button>
                </label>
                <p className="text-xs text-slate-500 mt-4">
                  {images.length}/10 Bilder hochgeladen
                </p>
              </div>

              {uploading && (
                <div className="mt-4 flex items-center justify-center gap-2 text-slate-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span>Bilder werden hochgeladen...</span>
                </div>
              )}
            </div>
          )}
        </form>
      </main>

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewImage?.filename}</DialogTitle>
          </DialogHeader>
          <div className="relative w-full bg-white rounded-lg" style={{ minHeight: '400px', maxHeight: '70vh' }}>
            {previewImage && (
              <ImageWithAuth
                src={`${API}/articles/${id}/images/${previewImage.id}/original?t=${Date.now()}`}
                alt={previewImage.filename}
                className="w-full h-full object-contain rounded-lg"
                token={localStorage.getItem("token")}
              />
            )}
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-slate-600">
              Verfügbare Auflösungen: Original, Medium (800x800), Thumbnail (200x200)
            </div>
            <Button
              variant="outline"
              onClick={() => setPreviewImage(null)}
              className="border-slate-300"
            >
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Images Dialog */}
      <Dialog open={downloadDialog.open} onOpenChange={(open) => !open && setDownloadDialog({ open: false, type: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bilder herunterladen</DialogTitle>
            <DialogDescription>
              Wählen Sie die Bildauflösungen für den Download
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700 font-medium mb-3 block">Bildauflösungen</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="size-original"
                    checked={selectedSizes.original}
                    onCheckedChange={(checked) => setSelectedSizes({ ...selectedSizes, original: checked })}
                  />
                  <label htmlFor="size-original" className="text-sm font-medium cursor-pointer">
                    Original (Volle Auflösung)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="size-medium"
                    checked={selectedSizes.medium}
                    onCheckedChange={(checked) => setSelectedSizes({ ...selectedSizes, medium: checked })}
                  />
                  <label htmlFor="size-medium" className="text-sm font-medium cursor-pointer">
                    Medium (800x800px)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="size-thumbnail"
                    checked={selectedSizes.thumbnail}
                    onCheckedChange={(checked) => setSelectedSizes({ ...selectedSizes, thumbnail: checked })}
                  />
                  <label htmlFor="size-thumbnail" className="text-sm font-medium cursor-pointer">
                    Thumbnail (200x200px)
                  </label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDownloadDialog({ open: false, type: null })}
              className="border-slate-300"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDownloadImages}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Image Confirmation Dialog */}
      <AlertDialog open={deleteImageDialog.open} onOpenChange={(open) => !open && setDeleteImageDialog({ open: false, imageId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bild löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie dieses Bild wirklich löschen? Alle Versionen (Original, Medium, Thumbnail) werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-image-cancel-button">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteImage}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-image-confirm-button"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
